import React, { lazy, Suspense, useContext } from "react";
import { Routes, Route } from "react-router-dom";
import Auth from "@xometry/auth-context";
import { NotFoundError } from "../../ErrorState";
import { ProposalProvider } from "../../proposals/contexts/proposalContext";
import { ModelViewerProvider } from "../../proposals/contexts/modelViewerContext";
import { ModalProvider } from "../../proposals/contexts/modalContext";
import PaymentsHeader from "../../PaymentsHeader";
import LoadingSpinner from "../../LoadingSpinner";
import Page from "../../../hooks/Page";
import StaffOnly from "./StaffOnly";
import OpportunityClaimRoute from "../../opportunity/OpportunityClaimRoute/OpportunityClaimRoute";
import CustomerRFQPage from "../../CustomerRFQ";
import { useConfig } from "../../proposals/contexts/configContext";
import { CustomerQuoteRequest } from "../../CustomerRFQ/CustomerRFQ";
import SingleFormView from "../../SingleFormView";
import { CoralogixProvider } from "../../../shared/CoralogixContext";

const Home = lazy(() => import("../../Home"));
const ProjectsTable = lazy(() => import("../../ProjectsTable"));
const DemoProposalPage = lazy(
  () => import("../../proposals/DemoProposalPage/DemoProposalPage")
);
const CreateProposalPage = lazy(
  () => import("../../proposals/CreateProposalPage/CreateProposalPage")
);
const ProposalRoute = lazy(() => import("./ProposalRoute"));
const OpportunityRoute = lazy(() => import("./OpportunityRoute"));
const VibePage = lazy(
  () => import("../../opportunity/ThomasVibePage/ThomasVibePage")
);

const OpportunityReviewRoute = lazy(
  () => import("../../opportunity/OpportunityReviewPage/OpportunityReviewPage")
);
const CreateOpportunityPage = lazy(
  () => import("../../opportunity/CreateOpportunityPage")
);
const OpportunityDemoPage = lazy(
  () => import("../../opportunity/OpportunityDemoPage/OpportunityDemoPage")
);
const CreateOpportunityFromJob = lazy(
  () => import("../../opportunity/CreateOpportunityFromJob")
);
const FormContainer = lazy(() => import("../../FormEditor/FormContainer"));
const TemplateContainer = lazy(
  () => import("../../StandaloneTemplate/TemplateContainer")
);
const TemplatePreview = lazy(() => import("../../TemplatePreview"));

const AppRoutes = (): React.ReactElement => {
  const { routerLocation } = useConfig();
  const { user } = useContext(Auth);
  return (
    <CoralogixProvider user={user}>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes location={routerLocation}>
          <Route
            path="/"
            element={
              <Page pageName="Marketplace Home">
                <StaffOnly>
                  <PaymentsHeader />
                  <Home />
                </StaffOnly>
              </Page>
            }
          />
          <Route
            path="/home/:ownerId"
            element={
              <Page pageName="Marketplace Home by Owner">
                <PaymentsHeader />
                <Home />
              </Page>
            }
          />
          <Route
            path="/demo"
            element={
              <Page pageName="Demo Proposal">
                <PaymentsHeader />
                <DemoProposalPage />
              </Page>
            }
          />
          <Route
            path="/create"
            element={
              <Page pageName="Create Proposal">
                <PaymentsHeader />
                <CreateProposalPage />
              </Page>
            }
          />
          <Route path="/project">
            <Route
              index
              element={
                <Page pageName="Marketplace Projects Home">
                  <ModalProvider>
                    <ProjectsTable />
                  </ModalProvider>
                </Page>
              }
            />
            <Route
              path="quote/:referenceId/*"
              element={
                <Page pageName="Proposal">
                  <ProposalProvider>
                    <ModalProvider>
                      <ModelViewerProvider>
                        <ProposalRoute />
                      </ModelViewerProvider>
                    </ModalProvider>
                  </ProposalProvider>
                </Page>
              }
            />
            <Route
              path=":projectId"
              element={
                <Page pageName="Marketplace Project">
                  <ModalProvider>
                    <OpportunityRoute />
                  </ModalProvider>
                </Page>
              }
            />
            <Route
              path=":projectId/review"
              element={
                <Page pageName="Marketplace Project Review">
                  <ModalProvider>
                    <OpportunityReviewRoute />
                  </ModalProvider>
                </Page>
              }
            />
            <Route
              path="create"
              element={
                <Page pageName="Create Marketplace Project" anonymous>
                  <CreateOpportunityPage />
                </Page>
              }
            />
            <Route
              path="demo"
              element={
                <Page pageName="Demo Project" anonymous>
                  <PaymentsHeader />
                  <OpportunityDemoPage />
                </Page>
              }
            />
            <Route path="create/from-job">
              <Route
                index
                element={
                  <Page pageName="Create Marketplace Project from Job">
                    <CreateOpportunityFromJob />
                  </Page>
                }
              />
              <Route
                path=":jobId"
                element={
                  <Page pageName="Create Marketplace Project from Job">
                    <CreateOpportunityFromJob />
                  </Page>
                }
              />
            </Route>
            <Route
              path="claim/:projectId"
              element={
                <Page pageName="Claim Marketplace Project" anonymous>
                  <OpportunityClaimRoute />
                </Page>
              }
            />
          </Route>
          <Route path="/rfq">
            <Route
              path=":vertical/:material/:projectId"
              element={
                <Page pageName="Marketplace Project">
                  <ModalProvider>
                    <VibePage />
                  </ModalProvider>
                </Page>
              }
            />
          </Route>
          <Route path="/form">
            <Route
              index
              element={
                <StaffOnly>
                  <FormContainer />
                </StaffOnly>
              }
            />
            <Route
              path=":formId"
              element={
                <StaffOnly>
                  <FormContainer />
                </StaffOnly>
              }
            />
          </Route>
          <Route path="/customer-quote-request" element={<CustomerRFQPage />} />
          <Route
            path="/customer-quote-request-modal"
            element={<CustomerQuoteRequest isModalOnly />}
          />
          <Route path="/single-form">
            <Route index element={<SingleFormView />} />
            <Route path=":formId" element={<SingleFormView />} />
          </Route>

          <Route path="/template">
            <Route
              index
              element={
                <StaffOnly>
                  <TemplateContainer />
                </StaffOnly>
              }
            />
            <Route
              path=":templateId"
              element={
                <StaffOnly>
                  <TemplateContainer />
                </StaffOnly>
              }
            />
            <Route
              path=":templateSlug/preview"
              element={
                <StaffOnly>
                  <Page pageName="Template Preview">
                    <TemplatePreview />
                  </Page>
                </StaffOnly>
              }
            />
          </Route>
          {/* Thomasnet Routes */}
          <Route
            path="/quote/:referenceId/*"
            element={
              <Page pageName="Thomasnet Quote">
                <ProposalProvider>
                  <ModelViewerProvider>
                    <ProposalRoute />
                  </ModelViewerProvider>
                </ProposalProvider>
              </Page>
            }
          />
          {/* End Thomasnet Routes */}
          <Route
            path="/:referenceId/*"
            element={
              <Page pageName="Proposal">
                <ProposalProvider>
                  <ModelViewerProvider>
                    <ProposalRoute />
                  </ModelViewerProvider>
                </ProposalProvider>
              </Page>
            }
          />
          <Route path="*" element={<NotFoundError />} />
        </Routes>
      </Suspense>
    </CoralogixProvider>
  );
};

export default AppRoutes;
